import React, { useEffect, useState } from "react";
import { FixedRangeDofLimitedCaptureProperty } from "../../../Model/CaptureProfile";
import DefaultImagingSettingTextBox from "./DefaultImagingSettingTextBox";
import "./FixedRangeOptions.css";
import {
  ELightPath,
  PlateTypeCaptureProfile,
  EDropLocationMethod,
  FocusCaptureProperty,
} from "../../../Model/CaptureProfile";
import CaptureProfileUtil from "../../../Utility/CaptureProfileUtil";
import { LightPathCapability } from "../../../Model/ImagerCapabilities";
import ImagerApi from "../../../api/ImagerApi";
const IMAGING_Z_MAX = "imagingZmax";
const IMAGING_Z_MIN = "imagingZmin";
const MAX_FOCUS_LEVELS = "maxFocusLevels";

function GetMaxFocusLevels(
  dof: number,
  depthOfFieldStepSizePercentage: number,
  imagingZmin: number,
  imagingZmax: number
) {
  const stepSize = dof * depthOfFieldStepSizePercentage;
  const totalDistance = Math.abs(imagingZmin - imagingZmax) + Math.abs(stepSize);
  return Math.floor(totalDistance / stepSize);
}

type Props = {
  style: React.CSSProperties;
  focusCaptureProperty: FocusCaptureProperty;
  fixedRangeDofCaptureProperty: FixedRangeDofLimitedCaptureProperty;
  onChange: (fixedRangeDofCaptureProperty: FixedRangeDofLimitedCaptureProperty) => void;
  lightPath: ELightPath;
  captureProfile: PlateTypeCaptureProfile;
  lighPathCapability: LightPathCapability;
  imagerIdentifier: string;
  onErrorOccur: (key: string) => void;
  onErrorCleared: (key: string) => void;
};

export default function FixedRangeDofLimitedOptions({
  style,
  fixedRangeDofCaptureProperty,
  onChange,
  lightPath,
  captureProfile,
  lighPathCapability,
  imagerIdentifier,
  focusCaptureProperty,
  onErrorOccur,
  onErrorCleared,
}: Props) {
  const [maxFocusLevels, setMaxFocusLevels] = useState(1);
  useEffect(() => {
    let zoom = 1;
    if (
      lightPath === ELightPath.Visible &&
      captureProfile.dropLocation?.dropLocationMethod !== EDropLocationMethod.Fixed
    ) {
      zoom = captureProfile.visible?.locateDrop?.dropLocationZoom ?? 1;
    } else {
      zoom = CaptureProfileUtil.getHardwareZoom(captureProfile, lightPath);
    }

    if (lighPathCapability.optic.continuousZoom) {
      ImagerApi.getContinuousZoomDof(imagerIdentifier, lightPath, zoom).then((dof) => {
        setMaxFocusLevels(
          GetMaxFocusLevels(
            dof,
            focusCaptureProperty.depthOfFieldStepSizePercentage,
            fixedRangeDofCaptureProperty.imagingZmin,
            fixedRangeDofCaptureProperty.imagingZmax
          )
        );
      });
    }

    if (lighPathCapability.optic.objectiveZoom) {
      const objective = lighPathCapability.optic.objectiveZoom.objectives.filter((o) => o.value === zoom)[0];
      setMaxFocusLevels(
        GetMaxFocusLevels(
          objective.depthOfField,
          focusCaptureProperty.depthOfFieldStepSizePercentage,
          fixedRangeDofCaptureProperty.imagingZmin,
          fixedRangeDofCaptureProperty.imagingZmax
        )
      );
    }
  }, [
    captureProfile,
    lightPath,
    imagerIdentifier,
    fixedRangeDofCaptureProperty.imagingZmax,
    fixedRangeDofCaptureProperty.imagingZmin,
    focusCaptureProperty.depthOfFieldStepSizePercentage,
    lighPathCapability.optic.continuousZoom,
    lighPathCapability.optic.objectiveZoom,
  ]);

  return (
    <div
      className="fixed-range-interpolated"
      style={{
        ...style,
      }}
    >
      <DefaultImagingSettingTextBox
        value={fixedRangeDofCaptureProperty.imagingZmin}
        onChange={(name, value) => onChange({ ...fixedRangeDofCaptureProperty, imagingZmin: value })}
        label="Start Z"
        name={IMAGING_Z_MIN}
        unit="mm"
        style={{
          gridArea: IMAGING_Z_MIN,
        }}
        disabled={false}
        isInteger={true}
        min={-50}
        max={50}
        onErrorOccur={(name) => onErrorOccur(name)}
        onErrorCleared={(name) => onErrorCleared(name)}
      />

      <DefaultImagingSettingTextBox
        value={fixedRangeDofCaptureProperty.imagingZmax}
        onChange={(name, value) => onChange({ ...fixedRangeDofCaptureProperty, imagingZmax: value })}
        label="Stop Z"
        name={IMAGING_Z_MAX}
        unit="mm"
        style={{
          gridArea: IMAGING_Z_MAX,
        }}
        disabled={false}
        isInteger={true}
        min={-50}
        max={50}
        onErrorOccur={(name) => onErrorOccur(name)}
        onErrorCleared={(name) => onErrorCleared(name)}
      />

      <DefaultImagingSettingTextBox
        value={maxFocusLevels}
        onChange={(name, value) => {}}
        label="Max. Focus Levels"
        name={MAX_FOCUS_LEVELS}
        unit=""
        style={{
          gridArea: MAX_FOCUS_LEVELS,
        }}
        disabled={true}
        isInteger={true}
        min={1}
        max={1000}
        onErrorOccur={(name) => onErrorOccur(name)}
        onErrorCleared={(name) => onErrorCleared(name)}
      />
    </div>
  );
}
